<script setup lang="ts">
import { computed } from 'vue';
import gettext from '@/i18n/gettext';
import type { CatalogueItem, CatalogueItemGroup } from '@/models/Catalogue';
import formatCost from '@/helpers/CurrencyHelper';

const { $gettext } = gettext;

export interface BomGroupItem {
  item: CatalogueItem;
  quantity: number;
}

const props = defineProps<{
  items: BomGroupItem[];
  groups: CatalogueItemGroup[];
}>();

const emit = defineEmits<{
  'item-click': [item: BomGroupItem];
}>();

const flatGroupsMap = computed<Record<string, { title: string; path: string }>>(
  () => {
    const map: Record<string, { title: string; path: string }> = {};
    const walk = (node: CatalogueItemGroup, path: string[]) => {
      const currentPath = [...path, node.name];
      map[node.uuid] = { title: node.name, path: currentPath.join(' / ') };
      node.children?.forEach((child) => walk(child, currentPath));
    };
    props.groups.forEach((g) => walk(g, []));
    return map;
  },
);

const sortedGroups = computed<[string, BomGroupItem[]][]>(() => {
  const grouped: Record<string, BomGroupItem[]> = {};
  for (const bi of props.items) {
    const key = bi.item.groupUuid || 'unknown';
    (grouped[key] ||= []).push(bi);
  }
  for (const key in grouped) {
    grouped[key].sort((a, b) => a.item.name.localeCompare(b.item.name));
  }
  return Object.entries(grouped).sort((a, b) => {
    const pathA = flatGroupsMap.value[a[0]]?.path || $gettext('Ungrouped');
    const pathB = flatGroupsMap.value[b[0]]?.path || $gettext('Ungrouped');
    return pathA.localeCompare(pathB);
  });
});

const groupCost = (items: BomGroupItem[]): number =>
  items.reduce((sum, bi) => sum + bi.item.cost * bi.quantity, 0);
</script>

<template>
  <v-expansion-panels multiple variant="accordion">
    <v-expansion-panel
      v-for="[groupUuid, groupItems] in sortedGroups"
      :key="groupUuid"
    >
      <v-expansion-panel-title>
        <v-icon icon="mdi-folder" class="mr-2" />
        <span class="text-subtitle-2">
          {{ flatGroupsMap[groupUuid]?.path || $gettext('Ungrouped') }}
        </span>
        <v-spacer />
        <span class="text-subtitle-2 text-medium-emphasis mr-4">
          {{ formatCost(groupCost(groupItems)) }}
        </span>
      </v-expansion-panel-title>
      <v-expansion-panel-text>
        <v-list density="compact">
          <v-list-item
            v-for="bomItem in groupItems"
            :key="bomItem.item.uuid"
            class="mb-2"
            @click="emit('item-click', bomItem)"
          >
            <v-list-item-title>{{ bomItem.item.name }}</v-list-item-title>
            <v-list-item-subtitle>
              <v-chip size="x-small" class="ml-2">
                {{ bomItem.item.category }}
              </v-chip>
            </v-list-item-subtitle>
            <template #append>
              <div class="d-flex align-center">
                <div class="text-right mr-4">
                  <div class="text-body-2">
                    {{ bomItem.quantity }} &times;
                    {{ formatCost(bomItem.item.cost) }}
                  </div>
                  <div class="text-caption text-medium-emphasis">
                    {{ $gettext('total') }}:
                    {{ formatCost(bomItem.item.cost * bomItem.quantity) }}
                  </div>
                </div>
                <slot name="item-actions" :bom-item="bomItem" />
              </div>
            </template>
          </v-list-item>
        </v-list>
      </v-expansion-panel-text>
    </v-expansion-panel>
  </v-expansion-panels>
</template>
